import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useUserFiles } from '../contexts/UserFilesContext';
import { useCollections } from '../contexts/CollectionsContext';

interface StatCard {
  label: string;
  value: string;
  color: 'soundwave' | 'neon';
  sublabel?: string;
  to?: string;
}

export const QuickStats: React.FC = () => {
  const { isProUser, isBetaUser } = useAuth();
  const { files, loading: filesLoading } = useUserFiles();
  const { collections, loading: collectionsLoading } = useCollections();

  const stats = useMemo(() => {
    const now = Date.now() / 1000;
    const sevenDaysAgo = now - 7 * 24 * 60 * 60;
    const sevenDaysFromNow = now + 7 * 24 * 60 * 60;
    const threeDaysFromNow = now + 3 * 24 * 60 * 60;

    const expiringCount = files.filter(f => f.ttl && f.ttl <= sevenDaysFromNow).length;
    const recentUploadCount = files.filter(f => {
      const uploadTime = typeof f.uploadedAt === 'number'
        ? f.uploadedAt
        : new Date(f.uploadedAt).getTime() / 1000;
      return uploadTime >= sevenDaysAgo;
    }).length;

    const soonExpiringTtls = files
      .filter(f => f.ttl && f.ttl > now && f.ttl <= threeDaysFromNow)
      .map(f => f.ttl as number);
    const minExpiryDays =
      soonExpiringTtls.length > 0
        ? Math.max(0, Math.floor((Math.min(...soonExpiringTtls) - now) / (24 * 60 * 60)))
        : undefined;

    return {
      totalFiles: files.length,
      expiringFiles: expiringCount,
      recentUploads: recentUploadCount,
      collectionsCount: collections.length,
      minExpiryDays,
      loading: filesLoading || collectionsLoading,
    };
  }, [files, collections, filesLoading, collectionsLoading]);

  const isPro = isProUser() || isBetaUser();

  const statCards: StatCard[] = [
    {
      label: 'Total Files',
      value: stats.loading ? '-' : stats.totalFiles.toString(),
      color: 'soundwave',
      to: '/my-files',
    },
    {
      label: isPro ? 'Recent Uploads' : 'Expiring Soon',
      value: stats.loading
        ? '-'
        : isPro
        ? stats.recentUploads.toString()
        : stats.expiringFiles.toString(),
      color: 'neon',
      sublabel: isPro
        ? 'Last 7 days'
        : !stats.loading && stats.minExpiryDays !== undefined
        ? `first expires in ${stats.minExpiryDays === 0 ? 'less than a day' : `${stats.minExpiryDays} day${stats.minExpiryDays === 1 ? '' : 's'}`}`
        : undefined,
      to: isPro ? '/my-files' : '/my-files?sort=expiring',
    },
    {
      label: 'Collections',
      value: stats.loading ? '-' : stats.collectionsCount.toString(),
      color: 'soundwave',
      to: '/collections',
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
      {statCards.map((stat, index) => {
        const isOrange = stat.color === 'soundwave';
        const interactive = !!stat.to;

        const tileBody = (
          <>
            <div
              className={`absolute inset-0 opacity-5 bg-gradient-to-br ${
                isOrange
                  ? 'from-soundwave-500 to-soundwave-600'
                  : 'from-neon-500 to-neon-600'
              }`}
            />

            <div className="relative z-10 min-w-0">
              <p
                className={`text-xs font-medium uppercase tracking-wide mb-1 transition-colors ${
                  interactive
                    ? isOrange
                      ? 'group-hover:text-soundwave-400'
                      : 'group-hover:text-neon-400'
                    : ''
                }`}
                style={{ color: 'var(--text-tertiary)' }}
              >
                {stat.label}
              </p>
              <p
                className={`text-2xl md:text-3xl font-bold truncate ${
                  isOrange ? 'text-soundwave-400' : 'text-neon-400'
                }`}
              >
                {stat.value}
              </p>
              {stat.sublabel && (
                <p
                  className="text-xs mt-1 opacity-70"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  {stat.sublabel}
                </p>
              )}
            </div>
          </>
        );

        const tileClass = `group relative rounded-xl overflow-hidden backdrop-blur-md border p-4 shadow-sm transition-shadow ${
          interactive ? 'hover:shadow-md cursor-pointer' : 'hover:shadow-md'
        }`;
        const tileStyle: React.CSSProperties = {
          backgroundColor: 'var(--background-surface)',
          borderColor: 'var(--border-color)',
        };

        if (interactive && stat.to) {
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="h-full"
            >
              <Link to={stat.to} className={`block h-full ${tileClass}`} style={tileStyle}>
                {tileBody}
              </Link>
            </motion.div>
          );
        }

        return (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            className={tileClass}
            style={tileStyle}
          >
            {tileBody}
          </motion.div>
        );
      })}
    </div>
  );
};
